import React, { useState, useEffect, useMemo, useCallback, memo, useRef } from 'react'
import B2BLayout from '../../../layouts/b2b'
import { Space, Button, Badge, Result, Form } from 'antd'
import { getColumns } from './columns'
import { createSelector } from 'reselect'
import { useDispatch, useSelector } from 'react-redux'
import fetchAPI, { IHttpError } from '../../../../lib/utils/fetch-api'
import { useNavigate, useLocation } from 'react-router-dom'
import FiltersList from '../../../components/v2/filters'
import { Sorter, State } from '../../../interfaces'
import { objectToUrl } from '../../utils'
import {
  currentFiltersProps,
  EnumElement,
  Enums,
  IEnum,
  IManagers,
  IOffice,
  IStage,
  TablePaginationPosition,
} from './interface'
import { getPrintPath } from '../../../components/v2/export-excel-button'
import {
  getJsonSettingsByKey,
  getColumnOrder,
  TableSettingsButton,
  TransferItem,
} from '../../../components/v2/table-settings-button'
import { OrderPrintButton } from '../ui/order-print-button/order-print-button'
import FiltersDrawer, { FiltersDrawerRef } from '../../../components/v2/filters-drawer'
import { FiltersFastButtons } from '../../../components/v2/filters-fast-buttons'
import DataTable from '../../../components/data-table'
import StickyToolbar from '../../../layouts/b2b/sticky-toolbar'
import { useHeaderActions } from '../../../contexts/header-actions-context'
import { HeaderAction } from '../../../contexts/header-actions-context/interface'
import { FilterOutlined, SettingOutlined } from '@ant-design/icons'
import { useMediaQuery } from 'react-responsive'
import { getMobileFields } from './mobile-fields'
import { getFilters } from './filters'
import { ResultStatusType } from 'antd/lib/result'
import { updateTourProgress } from '../../../store/session/action-creators'
import { resolveEnumCollectionIdByCode } from '../../../utilits/onboarding/open-enum-collection-by-code'
import { OrdersSetupTour } from '../../../utilits/onboarding/tours/orders-setup-tour'
import { hasPermissionScope } from 'src/react-app/lib/permissions'
import { Permissions } from 'src/react-app/constants/permissions'

const createSession = createSelector(
  (state: State) => state.session,
  sessionData => sessionData,
)

const OrdersList = () => {
  const navigate = useNavigate()
  const { pathname, search } = useLocation()
  const dispatch = useDispatch()
  const headerRef = useRef<HTMLDivElement | null>(null)
  const filtersDrawerRef = useRef<FiltersDrawerRef | null>(null)
  const tableSettingsRef = useRef<{ open: () => void } | null>(null)
  const { setHeaderActions } = useHeaderActions()
  const [filtersForm] = Form.useForm()
  const [data, setData] = useState([])
  const [offices, setOffices] = useState<IOffice[]>([])
  const [managers, setManagers] = useState<IManagers[]>([])
  const [zeroAmountCount, setZeroAmountCount] = useState<number>(0)
  const [availableFilters, setAvailableFilters] = useState<Enums>({
    manager: [],
    stage: [],
    cancel_reason: [],
    order_attribute: [],
    office: [],
    brand: [],
  })
  const [urlParams] = useState(Object.fromEntries(new URLSearchParams(search)))
  const [currentFilters, setCurrentFilters] = useState<currentFiltersProps>({
    phone: urlParams?.phone || undefined,
    manager: urlParams?.manager ? +urlParams.manager : undefined,
    id: urlParams.id ? +urlParams.id : undefined,
    stage: urlParams?.stage ? urlParams?.stage?.split(',') : undefined,
    dates_range: urlParams?.dates_range || undefined,
    dates_field: urlParams?.dates_field || undefined,
    cancel_reason: urlParams?.cancel_reason || undefined,
    order_attribute: urlParams?.order_attribute || undefined,
    customer_name: urlParams?.customer_name || undefined,
    production_number: urlParams?.production_number || undefined,
    brand: urlParams?.brand || undefined,
    office: urlParams?.office || undefined,
    contract_number: urlParams?.contract_number,
  })
  const { sessionData } = useSelector(createSession)
  const isLeaderByScope = hasPermissionScope(
    sessionData?.permissionsWithScope,
    Permissions.ACCESS_ORDERS,
    ['office', 'organization', 'brand', 'all'],
  )
  const isLeader = isLeaderByScope
  const sessionJsonSettings = (sessionData as any)?.jsonSettings || {}
  const tours = (sessionJsonSettings?.tours || {}) as Record<string, any>
  const orderAttributeTourId = 'setup.case_order_attribute'
  const customerTypeTourId = 'setup.customer_type_id'
  const [showSetupTour, setShowSetupTour] = useState(false)
  const [loading, setLoading] = useState(false)
  const [bottomCenter] = useState<TablePaginationPosition>('bottomCenter')
  const [stages, setStages] = useState<any>(null)
  const [sorter, setSorter] = useState<Sorter>({})
  const initialPageSize = (() => {
    const savedPageSize = localStorage.getItem('ordersPageSize')
    const parsedPageSize = Number(savedPageSize)
    return parsedPageSize && !isNaN(parsedPageSize) && parsedPageSize <= 150 ? parsedPageSize : 50
  })()

  const [pagination, setPagination] = useState({
    current: Number(urlParams?.page) || 1,
    pageSize: initialPageSize,
    position: [bottomCenter],
    showSizeChanger: true,
    total: 0,
  })

  const jsonSettings = useSelector(state => getJsonSettingsByKey(state, pathname))
  const isMobile = useMediaQuery({ maxWidth: 768 })

  interface IError {
    code?: ResultStatusType
    message?: string
  }
  const [error, setError] = useState<null | IError>(null)
  const isInitialLoadRef = useRef(true)
  const isLoadingStagesRef = useRef(false)
  const isLoadingFiltersRef = useRef(false)

  /**
   * Хлебные крошки
   */
  const breadcrumbs = useMemo(() => {
    return [
      {
        href: location.pathname,
        title: 'Заказы',
      },
    ]
  }, [])

  /**
   * Получает стадии
   */
  const getStages = useCallback(() => {
    if (!stages && !isLoadingStagesRef.current) {
      isLoadingStagesRef.current = true
      fetchAPI('/api/get-stages/order')
        .then(data => {
          setStages(data)
          isLoadingStagesRef.current = false
        })
        .catch(() => {
          console.error('Не удалось получить стадии')
          setError({ code: 'error', message: 'Не удалось получить стадии' })
          isLoadingStagesRef.current = false
        })
    }
  }, [stages])

  useEffect(() => {
    const params = new URLSearchParams(search)
    const nextFilters: currentFiltersProps = {
      phone: params.get('phone') || undefined,
      manager: params.get('manager') ? Number(params.get('manager')) : undefined,
      id: params.get('id') ? Number(params.get('id')) : undefined,
      stage: params.get('stage') ? params.get('stage')!.split(',') : undefined,
      dates_range: params.get('dates_range') || undefined,
      dates_field: params.get('dates_field') || undefined,
      cancel_reason: params.get('cancel_reason') || undefined,
      order_attribute: params.get('order_attribute') || undefined,
      customer_name: params.get('customer_name') || undefined,
      production_number: params.get('production_number') || undefined,
      brand: params.get('brand') || undefined,
      office: params.get('office') || undefined,
    }
    setCurrentFilters(nextFilters)
  }, [search])

  /**
   * Удаляет запись
   */
  const deleteRecord = useCallback(
    currentId => {
      fetchAPI(`/api/order/${currentId}`, { method: 'DELETE' })
        .then(() => {
          // Перезагружаем данные после удаления
          const urlParams = Object.fromEntries(new URLSearchParams(search))
          fetchAPI(`/api/orders${objectToUrl(urlParams)}`)
            .then(({ data, pagination: paginationData, sorter: sorterData }) => {
              if (data && stages) {
                setData(
                  data.map(item => ({
                    ...item,
                    stage: stages.find(stage => stage.code === item.stage),
                  })),
                )
                setPagination(prevState => ({
                  ...prevState,
                  ...paginationData,
                }))
                if (sorterData) {
                  setSorter(sorterData)
                }
              }
            })
            .catch(() => {
              console.error('Не удалось обновить список после удаления')
            })
        })
        .catch(() => {
          console.error('Не удалось удалить запись')
        })
    },
    [search, stages],
  )

  /**
   * Получает список доступных фильтров для обращений
   */
  const getAvailableFilters = useCallback(() => {
    if (!stages || stages.length === 0 || isLoadingFiltersRef.current) {
      return
    }
    isLoadingFiltersRef.current = true
    const enumsList = {
      types: ['managers', 'case_cancel_reason', 'case_order_attribute', 'offices', 'brands'],
    }
    const currentUrlParams = Object.fromEntries(new URLSearchParams(location.search))
    const selectedBrand = currentUrlParams?.brand
    const selectedOffice = currentUrlParams?.office
    fetchAPI(`/api/enum-list${objectToUrl(enumsList)}`)
      .then(data => {
        const officesList = selectedBrand
          ? data?.offices
              ?.filter(i => String(i.brand_id) === String(selectedBrand))
              ?.map((i: IEnum) => ({
                label: i.value,
                value: i.id,
              }))
          : data?.offices?.map((i: IEnum) => ({ label: i.value, value: i.id }))
        const managersList =
          selectedOffice || selectedBrand
            ? data?.managers
                ?.filter(i =>
                  selectedOffice
                    ? String(i.office_id) === String(selectedOffice)
                    : String(i.brand_id) === String(selectedBrand),
                )
                ?.map((i: IEnum) => ({ label: i.value, value: i.id }))
            : data?.managers?.map((i: IEnum) => ({ label: i.value, value: i.id }))
        const filters = {
          stage: stages.map((i: IStage) => ({ label: i.name, value: i.code })),
          manager: managersList,
          cancel_reason: data?.case_cancel_reason?.map((i: IEnum) => ({
            label: i.value,
            value: i.id,
          })),
          order_attribute: data?.case_order_attribute?.map((i: IEnum) => ({
            label: i.value,
            value: i.id,
          })),
          brand: data?.brands?.map((i: IEnum) => ({ label: i.value, value: String(i.id) })),
          office: officesList,
        }
        setOffices(data?.offices)
        setManagers(data?.managers)
        setAvailableFilters(filters)
        isLoadingFiltersRef.current = false
      })
      .catch(() => {
        console.error('Не удалось получить список доступных фильтров')
        isLoadingFiltersRef.current = false
      })
  }, [stages])

  /**
   * Получает записи
   */
  const getData = useCallback(async () => {
    if (!stages || stages.length === 0) {
      return
    }
    try {
      setLoading(true)
      setError(null)
      const urlParams = Object.fromEntries(new URLSearchParams(search))
      await fetchAPI(`/api/orders${objectToUrl(urlParams)}`).then(
        ({ data, pagination, sorter, stats }) => {
          if (data) {
            setData(
              data.map(item => {
                return {
                  ...item,
                  stage: stages.find(stage => stage.code === item.stage),
                }
              }),
            )
            setPagination(prevState => ({
              ...prevState,
              ...pagination,
            }))
            setSorter(sorter)
            if (stats?.zero_amount_count !== undefined) {
              setZeroAmountCount(stats.zero_amount_count)
            }
          }
        },
      )
    } catch (error) {
      console.error('Ошибка при получении данных:', error)
      setError({
        message: (error as IHttpError)?.message || 'Неизвестная ошибка',
        code: ((error as IHttpError)?.statusCode || 'error') as ResultStatusType,
      })
    } finally {
      setLoading(false)
    }
  }, [stages, search])

  // Загружаем stages при первом монтировании
  useEffect(() => {
    if (!stages) {
      getStages()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stages])

  // Загружаем фильтры когда stages готовы
  useEffect(() => {
    if (stages && stages.length > 0 && isInitialLoadRef.current) {
      getAvailableFilters()
      isInitialLoadRef.current = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stages])

  // Загружаем данные когда stages готовы или изменяются параметры поиска
  useEffect(() => {
    if (stages && stages.length > 0) {
      getData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, stages])

  useEffect(() => {
    const userId = sessionData?.user?.id
    if (!userId) return
    if (pathname !== '/orders') return
    const needsOrderAttribute = !tours?.[orderAttributeTourId]?.completed
    const needsCustomerType = !tours?.[customerTypeTourId]?.completed
    if (!needsOrderAttribute && !needsCustomerType) return

    const nextTourId = needsOrderAttribute ? orderAttributeTourId : customerTypeTourId
    const nextStage = tours?.[nextTourId]?.stage

    setShowSetupTour(true)
    if (nextStage !== 'prompted') {
      dispatch(updateTourProgress(nextTourId, { stage: 'prompted' }) as any)
    }
  }, [
    pathname,
    sessionData?.user?.id,
    tours?.[orderAttributeTourId]?.completed,
    tours?.[orderAttributeTourId]?.stage,
    tours?.[customerTypeTourId]?.completed,
    tours?.[customerTypeTourId]?.stage,
  ])

  const openNextOrdersDictionary = useCallback(async () => {
    const needsOrderAttribute = !tours?.[orderAttributeTourId]?.completed
    const nextCode = needsOrderAttribute ? 'case_order_attribute' : 'customer_type_id'
    const nextTourId = needsOrderAttribute ? orderAttributeTourId : customerTypeTourId

    const id = await resolveEnumCollectionIdByCode(nextCode)
    setShowSetupTour(false)
    if (!id) return

    dispatch(updateTourProgress(nextTourId, { stage: 'opened', collectionId: id }) as any)
    navigate(`/admin/enum-collection/${id}`, {
      state: {
        onboardingTourId: nextTourId,
        onboardingCollectionCode: nextCode,
      },
    })
  }, [dispatch, navigate, tours])

  /**
   * Отслеживает изменение состояния таблицы
   *
   * @param pagination
   * @param filters
   * @param sorter
   */
  const handleTableChange = useCallback(
    (_pagination, _filters, sorter) => {
      const currentUrlParams = Object.fromEntries(new URLSearchParams(search))
      setPagination(prevState => ({ ...prevState, ...pagination }))

      const current: Record<string, any> = {
        ...currentUrlParams,
        sort_order: sorter?.order,
        sort_field: sorter?.field,
        page: pagination.current,
        pageSize: pagination.pageSize,
      }
      if (current?.page === 1) {
        delete current.page
      }
      if (current?.pageSize === 10) {
        delete current.pageSize
      }
      if (current?.sort_order === undefined) {
        delete current.sort_order
        delete current.sort_field
      }
      const urlParams = new URLSearchParams(current).toString()
      navigate({ search: urlParams })
    },
    [search, navigate, pagination],
  )

  /**
   * Отправляет на страницу деталки
   * @param record
   */
  const handleRowClick = record => {
    const { id } = record
    navigate(`/order/${id}`)
  }

  /**
   * Отслеживает состояние фильтров
   * @param filters
   */
  const changeFiltersHandler = useCallback(
    (filters, isButtonChange = false) => {
      if (!filters?.dates_range) {
        delete filters?.dates_field
      }
      const selectedBrand = filters?.brand
      const selectedOffice = filters?.office
      if (selectedBrand) {
        setAvailableFilters(filters => {
          const office = offices
            ?.filter(i => String(i.brand_id) === String(selectedBrand))
            ?.map(
              i =>
                ({
                  label: i.value,
                  value: i.id,
                } as EnumElement),
            )
          const manager =
            selectedOffice || selectedBrand
              ? managers
                  ?.filter(i =>
                    selectedOffice
                      ? String(i.office_id) === String(selectedOffice)
                      : String(i.brand_id) === String(selectedBrand),
                  )
                  ?.map(
                    i =>
                      ({
                        label: i.value,
                        value: i.id,
                      } as EnumElement),
                  )
              : managers?.map(
                  i =>
                    ({
                      label: i.value,
                      value: i.id,
                    } as EnumElement),
                )
          return {
            ...filters,
            office,
            manager,
          }
        })
      }
      if (isButtonChange) {
        const normalizedStage =
          typeof filters?.stage === 'string' ? String(filters.stage).split(',') : filters?.stage
        const normalized = { ...filters, stage: normalizedStage }
        setCurrentFilters(prevState => ({
          ...prevState,
          ...normalized,
        }))
      }
      const tmpFilter: Record<string, any> = { ...(filters || {}), pageSize: pagination.pageSize }
      if (tmpFilter.stage === '' || tmpFilter.stage === undefined || tmpFilter.stage === null) {
        delete tmpFilter.stage
      }
      const newUrlParams = new URLSearchParams(tmpFilter).toString()
      navigate({ search: newUrlParams })
    },
    [pagination, offices, managers, setData],
  )

  const dataWithActions = useMemo(() => {
    return data.map(item => ({
      ...(item as any),
      deleteRecord,
    }))
  }, [data, deleteRecord])

  const filters = useMemo(() => {
    const isLeader =
      hasPermissionScope(sessionData?.permissionsWithScope, Permissions.ACCESS_ORDERS, [
        'office',
        'organization',
        'brand',
        'all',
      ])
    return getFilters({
      currentFilters,
      availableFilters,
      isLeader,
    })
  }, [availableFilters, currentFilters, sessionData?.permissionsWithScope])

  const columns = useMemo(() => getColumns(isLeader, sorter), [isLeader, sorter])
  const viewColumns = useMemo(() => {
    const columnOrder = getColumnOrder(jsonSettings)
    if (columnOrder.length > 0) {
      const result: any[] = []
      for (let i = 0; i < columnOrder.length; i++) {
        const item = columns.find(column => columnOrder[i] === column?.['dataIndex'])
        if (item) {
          result.push(item)
        }
      }
      const actionColumn = columns.find((column: any) => column.key === 'operation')
      if (actionColumn && !result.find((col: any) => col?.key === 'operation')) {
        result.push(actionColumn)
      }
      return result
    }
    return columns
  }, [columns, jsonSettings])

  const excelPath = useMemo(() => {
    return getPrintPath('/api/orders', new URLSearchParams(search))
  }, [search])

  const settingsColumns = useMemo(
    () =>
      columns
        .filter((column: any) => column?.key !== 'operation')
        .map(column => ({
          key: column?.['dataIndex'],
          title: column?.title,
        })),
    [columns],
  ) as TransferItem[]

  const mobileFields = useMemo(() => getMobileFields(), [])

  const isZeroAmountFilterActive = useMemo(() => {
    const params = new URLSearchParams(search)
    return params.get('zero_amount') === 'y'
  }, [search])

  const handleZeroAmountFilterClick = useCallback(() => {
    const params = new URLSearchParams(search)
    if (isZeroAmountFilterActive) {
      params.delete('zero_amount')
    } else {
      params.set('zero_amount', 'y')
    }
    params.delete('page')
    navigate({ search: params.toString() })
  }, [search, isZeroAmountFilterActive, navigate])

  const additionalFastFilters = useMemo<
    Array<{ name: string; code: string; value: string; style?: React.CSSProperties }>
  >(() => {
    const filters: Array<{
      name: string
      code: string
      value: string
      style?: React.CSSProperties
    }> = []

    if (sessionData?.user?.id) {
      filters.push({
        name: 'Мои заказы',
        code: 'manager',
        value: String(sessionData.user.id),
      })
    }

    return filters
  }, [sessionData?.user?.id])

  const handlePageChange = (page: number, pageSize: number) => {
    setPagination(prevState => ({
      ...prevState,
      current: Number(page) || 1,
      pageSize: pageSize > 150 ? 150 : pageSize,
    }))
    if (!isNaN(pageSize)) {
      localStorage.setItem('ordersPageSize', String(pageSize))
    }
    const currentUrlParams = new URLSearchParams(search)
    if (page && page !== 1) {
      currentUrlParams.set('page', page.toString())
    } else {
      currentUrlParams.delete('page')
    }
    if (!isNaN(pageSize)) {
      if (pageSize && pageSize !== 10) {
        currentUrlParams.set('pageSize', pageSize.toString())
      } else {
        currentUrlParams.delete('pageSize')
      }
    }
    navigate({ search: currentUrlParams.toString() })
  }

  useEffect(() => {
    const actions: HeaderAction[] = []
    if (filtersDrawerRef) {
      actions.push({
        key: 'filters',
        icon: <FilterOutlined />,
        onClick: () => {
          filtersDrawerRef.current?.toggle()
        },
        tooltip: 'Фильтры',
      })
    }
    if (isMobile) {
      actions.push({
        key: 'settings',
        icon: <SettingOutlined />,
        onClick: () => {
          tableSettingsRef.current?.open()
        },
        tooltip: 'Настройка колонок',
      })
    }
    setHeaderActions(actions)
    return () => {
      setHeaderActions([])
    }
  }, [setHeaderActions, isMobile])

  return (
    <B2BLayout breadcrumbs={breadcrumbs} isViewScrollButton pageTitle={'Заказы'}>
      <OrdersSetupTour
        open={showSetupTour}
        onClose={() => setShowSetupTour(false)}
        headerTarget={() => headerRef.current || document.body}
        onOpenDictionary={openNextOrdersDictionary}
      />
      {error && <Result status={error.code} title="Ошибка" subTitle={error.message} />}
      {!error && stages && (
        <>
          <StickyToolbar
            ref={headerRef}
            collapsible={
              <Space
                style={{ width: '100%', justifyContent: 'space-between' }}
                align={'start'}
                wrap
              >
                <Space align={'start'} wrap>
                  <FiltersList
                    filters={filters.filter(f =>
                      ['id', 'stage', 'production_number'].includes(f.name),
                    )}
                    onChange={changeFiltersHandler}
                    isChangeByButtons
                    form={filtersForm}
                  />
                  <Badge count={zeroAmountCount} showZero={false} overflowCount={999}>
                    <Button
                      type={isZeroAmountFilterActive ? 'primary' : 'default'}
                      onClick={handleZeroAmountFilterClick}
                    >
                      Заказы без суммы
                    </Button>
                  </Badge>
                </Space>
                <FiltersDrawer
                  filters={filters}
                  onChange={changeFiltersHandler}
                  currentFilters={
                    {
                      ...currentFilters,
                      stage: currentFilters?.stage ? currentFilters.stage.join(',') : undefined,
                    } as any
                  }
                  ref={filtersDrawerRef}
                />
              </Space>
            }
            persistent={
              isMobile ? (
                <FiltersFastButtons
                  filters={[
                    ...additionalFastFilters,
                    ...(stages || []).map((s: any) => ({
                      name: s.name,
                      code: 'stage',
                      value: s.code,
                      style: (s as any).style,
                    })),
                  ]}
                  onChange={changeFiltersHandler}
                />
              ) : (
                <Space
                  style={{ width: '100%', justifyContent: 'space-between' }}
                  align={'center'}
                  wrap
                >
                  <FiltersFastButtons
                    filters={[
                      ...additionalFastFilters,
                      ...(stages || []).map((s: any) => ({
                        name: s.name,
                        code: 'stage',
                        value: s.code,
                        style: (s as any).style,
                      })),
                    ]}
                    onChange={changeFiltersHandler}
                  />
                  <Space align={'center'} wrap>
                    <OrderPrintButton path={excelPath} columns={viewColumns} stages={stages} />
                    <TableSettingsButton items={settingsColumns} ref={tableSettingsRef as any} />
                  </Space>
                </Space>
              )
            }
          />
          <DataTable
            columns={viewColumns}
            mobileFields={mobileFields}
            data={dataWithActions}
            loading={loading}
            rowKey={r => String(r.id)}
            highlightLastRowPrefix={'order'}
            pagination={pagination}
            onRowClick={handleRowClick}
            onPageChange={handlePageChange}
            onTableChange={handleTableChange}
            className={'orders-table'}
            headerRef={headerRef}
            maxLines={1}
            bordered
            scroll={{ x: 'max-content' }}
          />
        </>
      )}
    </B2BLayout>
  )
}

export default memo(OrdersList)
